import { useSuspenseQuery } from '@apollo/client';
import {
  GateDataForRenderer,
  GET_GATE_RENDERER_DATA,
  GET_GATE_RENDERER_DATA_FOR_METRICS_VIEW,
} from '../graphql/queries/getGateRendererData';
import { getMovementFragmentData } from '../utils/getMovementFragmentData';
import { useMemo } from 'react';

export type UseGateCommonDataQueryOptions = {
  fetchPolicy?: 'no-cache';
};

export type GateCommonData = ReturnType<typeof useGateData>;

const convertCommonData = (gate: GateDataForRenderer) => {
  const lastPosition = gate.movements2.lastPosition ?? null;
  const movements = gate.movements2.history.map(getMovementFragmentData);
  const {
    activeMovement: activeMovementFragment,
    lastMovement: lastMovementFragment,
    operatingState,
    unit,
    precision,
  } = gate.movements2;
  const lastMovement = lastMovementFragment ? getMovementFragmentData(lastMovementFragment) : null;
  const activeMovement = activeMovementFragment
    ? getMovementFragmentData(activeMovementFragment)
    : null;

  return {
    operatingState,
    lastPosition,
    movements,
    activeMovement,
    lastMovement,
    unit,
    precision,
  };
};

export const useGateData = (gateId: string, queryOptions?: UseGateCommonDataQueryOptions) => {
  const { data } = useSuspenseQuery(GET_GATE_RENDERER_DATA, {
    variables: { gateId },
    ...queryOptions,
  });

  return useMemo(() => {
    return convertCommonData(data.gate);
  }, [data]);
};

export const useGateDataForMetricsView = (
  gateId: string,
  viewId: string,
  queryOptions?: UseGateCommonDataQueryOptions,
) => {
  const { data } = useSuspenseQuery(GET_GATE_RENDERER_DATA_FOR_METRICS_VIEW, {
    variables: { gateId, viewId },
    ...queryOptions,
  });
  return useMemo(() => {
    const gate = data.metricsViewSection.device.gate;
    if (!gate)
      throw new Error(
        `Invalid access to gate from device with id ${data.metricsViewSection.device.id}`,
      );
    return convertCommonData(gate);
  }, [data]);
};
